import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { publicApi } from "../admin/api";
import { usePageContent } from "../lib/usePageContent";
import { reopenCookieBanner } from "../lib/useCookieConsent";

const DEFAULTS = {
  address: "Via della Moscova 18, 20121 Milano, Italia",
  phone: "+39 02 0000 0000",
  email: "info@investhospitality.it",
  press_email: "press@investhospitality.it",
  hours: "Lun–Ven · 9.00–18.30",
  linkedin: "https://www.linkedin.com",
  instagram: "https://www.instagram.com",
  copyright_name: "Invest Hospitality S.r.l.",
  built_by: "Ptk",
  built_by_url: "",
  privacy_url: "#",
  cookie_url: "#",
  legal_url: "#",
};

const DEFAULT_TAGLINE =
  "Holding italiana indipendente. Dal 2014 sviluppiamo, costruiamo e gestiamo hotel urbani a Milano con un modello integrato, dalla progettazione all'accoglienza quotidiana.";

export default function Footer() {
  const [s, setS] = useState(DEFAULTS);
  const c = usePageContent("footer", {});

  useEffect(() => {
    publicApi
      .get("/settings")
      .then((r) => setS({ ...DEFAULTS, ...r.data }))
      .catch(() => {});
  }, []);

  return (
    <footer
      data-testid="main-footer"
      className="relative mt-32 border-t border-white/5 bg-[#060608]"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <img
              src="/brand/invest-hospitality-logo.svg"
              alt="Invest Hospitality"
              className="h-12 w-auto"
            />
            <p className="mt-6 text-white/50 max-w-md leading-relaxed">
              {c.tagline || DEFAULT_TAGLINE}
            </p>
            <div className="mt-8 flex gap-3">
              {s.linkedin && (
                <a
                  href={s.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="h-10 w-10 grid place-items-center rounded-full border border-white/10 hover:bg-white hover:text-black transition-colors"
                  aria-label="LinkedIn"
                  data-testid="footer-linkedin"
                >
                  <Linkedin size={16} />
                </a>
              )}
              {s.instagram && (
                <a
                  href={s.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="h-10 w-10 grid place-items-center rounded-full border border-white/10 hover:bg-white hover:text-black transition-colors"
                  aria-label="Instagram"
                  data-testid="footer-instagram"
                >
                  <Instagram size={16} />
                </a>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">
              Navigazione
            </p>
            <ul className="mt-5 space-y-3 text-white/70">
              <li>
                <Link to="/chi-siamo" className="hover:text-white">
                  Chi Siamo
                </Link>
              </li>
              <li>
                <Link to="/hotel" className="hover:text-white">
                  Portafoglio
                </Link>
              </li>
              <li>
                <Link to="/nuove-aperture" className="hover:text-white">
                  Nuove Aperture
                </Link>
              </li>
              <li>
                <Link to="/news" className="hover:text-white">
                  News & Insights
                </Link>
              </li>
              <li>
                <Link to="/investor" className="hover:text-white">
                  Area Investitori
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">
              Contatti
            </p>
            <ul className="mt-5 space-y-4 text-white/70">
              {s.address && (
                <li className="flex gap-3">
                  <MapPin size={16} className="mt-1 text-[#C48F56] shrink-0" />
                  <span data-testid="footer-address">{s.address}</span>
                </li>
              )}
              {s.phone && (
                <li className="flex gap-3">
                  <Phone size={16} className="mt-1 text-[#C48F56] shrink-0" />
                  <a
                    href={`tel:${s.phone.replace(/\s+/g, "")}`}
                    className="hover:text-white"
                    data-testid="footer-phone"
                  >
                    {s.phone}
                  </a>
                </li>
              )}
              {s.email && (
                <li className="flex gap-3">
                  <Mail size={16} className="mt-1 text-[#C48F56] shrink-0" />
                  <a
                    href={`mailto:${s.email}`}
                    className="hover:text-white"
                    data-testid="footer-email"
                  >
                    {s.email}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-white/40">
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} {s.copyright_name} — Tutti i diritti
            riservati.
            {s.built_by && (
              <>
                {" · "}
                Built by{" "}
                {s.built_by_url ? (
                  <a
                    href={s.built_by_url}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-white underline-offset-4 hover:underline"
                  >
                    {s.built_by}
                  </a>
                ) : (
                  <span className="text-white/60">{s.built_by}</span>
                )}
              </>
            )}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/privacy-policy" className="hover:text-white">
              Privacy
            </Link>
            <Link to="/cookie-policy" className="hover:text-white">
              Cookie
            </Link>
            <button
              type="button"
              onClick={reopenCookieBanner}
              data-testid="footer-manage-cookies"
              className="hover:text-white text-left"
            >
              Gestisci preferenze
            </button>
            {s.legal_url && (
              <a href={s.legal_url} className="hover:text-white">
                Note legali
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
